import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosProgressEvent, AxiosResponse, isAxiosError } from 'axios';
import { eventChannel } from 'redux-saga';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { httpDELETE, httpGET, httpPOST, httpPUT } from '../../services/http.ts';
import { UploadProgress } from '../../types/file.ts';
import { Folder } from '../../types/folder.ts';
import { Project } from '../../types/project.ts';
import { projectsReducer } from '../reducers/projects.reducer.ts';
import { toastReducer } from '../reducers/toast.reducer.ts';
import { uiReducer } from '../reducers/ui.reducer.ts';
import { setProject } from '../reducers/workspace.reducer.ts';
import {
  getCurrentFolderId,
  getCurrentProject,
  getProjectsHasNext,
  getProjectsPage,
} from '../selectors/projects.selector.ts';
import { withLoading } from './ui.saga.ts';

const {
  loadProjects,
  loadExampleProjects,
  cloneExampleProject,
  setProjectsPage,
  appendProjectsPage,
  resetProjects,
  setCurrentFolderId,
  loadMoreProjects,
  createProject,
  updateProject,
  deleteProject,
  removeProject,
  renameProject,
  moveProject,
  loadFolder,
  setFolder,
  loadFolderTree,
  setFolderTree,
  createFolder,
  updateFolder,
  deleteFolder,
  updateProjectName,
  updateProjectTemplate,
  setProjectById,
  setExampleProjects,
} = projectsReducer.actions;
const { showToast } = toastReducer.actions;

interface UpdateProjectPayload {
  projectId: number;
  newName: string;
}

interface MoveProjectPayload {
  projectId: number;
  targetFolderId: number | null;
}

interface CreateFolderPayload {
  folderName: string;
  parentId: number | null;
}

interface UpdateFolderPayload {
  folderId: number;
  newName: string;
}

interface UpdateProjectTemplatePayload {
  projectId: number;
  template: boolean;
}

type ProjectsPageResponse = {
  content: Project[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
};

function* loadProjectsAction(action: PayloadAction<number | undefined>) {
  yield call(withLoading, 'loadProjects', function* () {
    const folderId = action.payload ? action.payload : '';
    // reset and load first page
    yield put(resetProjects());
    yield put(setCurrentFolderId(action.payload));
    const { data } = (yield call(
      httpGET,
      `/projects?folderId=${folderId}&page=0`,
    )) as AxiosResponse<ProjectsPageResponse>;
    yield put(
      setProjectsPage({
        content: data.content,
        page: data.page,
        pageSize: data.pageSize,
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        hasNext: data.hasNext,
      }),
    );
  });
}

function* loadMoreProjectsAction() {
  yield call(withLoading, 'loadMoreProjects', function* () {
    // Read current pagination and folder from state
    const page = (yield select(getProjectsPage)) as number;
    const hasNext = (yield select(getProjectsHasNext)) as boolean;
    const currentFolderId = (yield select(getCurrentFolderId)) as number | undefined;

    if (!hasNext) return;

    const folderId = currentFolderId ? currentFolderId : '';
    const nextPage = page + 1;
    const { data } = (yield call(
      httpGET,
      `/projects?folderId=${folderId}&page=${nextPage}`,
    )) as AxiosResponse<ProjectsPageResponse>;
    yield put(
      appendProjectsPage({
        content: data.content,
        page: data.page,
        pageSize: data.pageSize,
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        hasNext: data.hasNext,
      }),
    );
  });
}

function* loadExampleProjectsAction() {
  yield call(withLoading, 'loadExampleProjects', function* () {
    const { data } = (yield call(httpGET, `/projects/examples`)) as AxiosResponse<Project[]>;
    yield put(setExampleProjects(data));
  });
}

function* cloneExampleProjectAction(action: PayloadAction<number>) {
  const exampleProjectId = action.payload;
  yield call(withLoading, 'cloneExampleProject', function* () {
    try {
      const { data } = (yield call(
        httpPOST,
        `/projects/examples/${exampleProjectId}/clone`,
        {},
      )) as AxiosResponse<Project>;
      // redirect to workspace with new project id
      window.location.href = `/workspace?projectId=${data.id}`;
    } catch (error: unknown) {
      const errorMessage =
        isAxiosError(error) && error.response?.data ? error.response.data : 'projects.toast.cloneError';
      yield put(showToast({ message: errorMessage, type: 'error' }));
    }
  });
}

function* createProjectAction(action: PayloadAction<FormData>) {
  const formData = action.payload;
  const { setUploadProgress } = uiReducer.actions;

  yield call(withLoading, 'createProject', function* () {
    try {
      const progressChannel = eventChannel((emitter) => {
        const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            emitter({
              totalSize: progressEvent.total,
              uploadedSize: progressEvent.loaded,
              isComplete: progressEvent.loaded === progressEvent.total,
            });
          }
        };

        httpPOST(`/projects`, formData, { onUploadProgress })
          .then((response) => emitter({ response }))
          .catch((error) => emitter({ error }));

        return () => {};
      });

      while (true) {
        const payload = (yield take(progressChannel)) as
          | UploadProgress
          | { response: AxiosResponse<Project> }
          | { error: unknown };
        if ('response' in payload) {
          const { data } = payload.response;
          yield put(setUploadProgress(null));
          window.location.href = `/workspace?projectId=${data.id}`;
          break;
        } else if ('error' in payload) {
          const error = payload.error;
          yield put(setUploadProgress(null));
          if (isAxiosError(error) && error.response?.status === 413) {
            yield put(showToast({ message: 'projects.toast.modelsTooLarge', type: 'error' }));
          } else {
            const errorMessage =
              isAxiosError(error) && error.response?.data ? error.response.data : 'projects.toast.createError';
            yield put(showToast({ message: errorMessage, type: 'error' }));
          }
          break;
        } else {
          yield put(setUploadProgress(payload));
        }
      }
    } catch (error: unknown) {
      yield put(setUploadProgress(null));
      throw error;
    }
  });
}

function* updateProjectAction(action: PayloadAction<UpdateProjectPayload>) {
  const updateProjectPayload = action.payload;
  yield call(withLoading, 'updateProject', function* () {
    try {
      yield call(httpPUT, `/projects/${updateProjectPayload.projectId}`, { name: updateProjectPayload.newName });
      yield put(renameProject(updateProjectPayload));
      yield put(showToast({ message: 'workspace.projects.toast.projectUpdated', type: 'success' }));
    } catch (error: unknown) {
      const errorMessage =
        isAxiosError(error) && error.response?.data ? error.response.data : 'workspace.projects.toast.updateError';
      yield put(showToast({ message: errorMessage, type: 'error' }));
    }
  });
}

function* moveProjectAction(action: PayloadAction<MoveProjectPayload>) {
  const moveProjectPayload = action.payload;
  yield call(withLoading, 'moveProject', function* () {
    try {
      yield call(httpPUT, `/projects/${moveProjectPayload.projectId}/move`, {
        targetFolderId: moveProjectPayload.targetFolderId,
      });
      // Only remove from current view if moving to a different folder
      const currentFolderId = (yield select(
        (state: { projects: { currentFolderId?: number } }) => state.projects.currentFolderId,
      )) as number | undefined;
      const targetId = moveProjectPayload.targetFolderId ?? undefined;
      if (targetId !== currentFolderId) {
        yield put(removeProject(moveProjectPayload.projectId));
      }
      yield put(showToast({ message: 'projects.toast.projectMoved', type: 'success' }));
    } catch (error: unknown) {
      const errorMessage =
        isAxiosError(error) && error.response?.data ? error.response.data : 'projects.toast.moveError';
      yield put(showToast({ message: errorMessage, type: 'error' }));
    }
  });
}

function* deleteProjectAction(action: PayloadAction<number>) {
  const projectId = action.payload;
  yield call(withLoading, 'deleteProject', function* () {
    try {
      yield call(httpDELETE, `/projects/${projectId}`, {});
      yield put(showToast({ message: 'projects.toast.projectDeleted', type: 'success' }));
      yield put(removeProject(projectId));
    } catch (error) {
      console.error('Failed to delete project', error);
      yield put(showToast({ message: 'projects.toast.deleteError', type: 'error' }));
    }
  });
}

function* loadFolderAction(action: PayloadAction<number | undefined>) {
  yield call(withLoading, 'loadFolder', function* () {
    const folderId = action.payload ? action.payload : '';
    const { data } = (yield call(httpGET, `/folders?folderId=${folderId}`)) as AxiosResponse<Folder>;
    yield put(setFolder(data));
  });
}

function* loadFolderTreeAction(action: PayloadAction<number | undefined>) {
  yield call(withLoading, 'loadFolderTree', function* () {
    const folderId = action.payload ? action.payload : '';
    const { data } = (yield call(httpGET, `/folders?folderId=${folderId}`)) as AxiosResponse<Folder>;
    yield put(setFolderTree(data));
  });
}

function* updateFolderAction(action: PayloadAction<UpdateFolderPayload>) {
  const updateFolderPayload = action.payload;
  yield call(withLoading, 'updateFolder', function* () {
    try {
      yield call(httpPUT, `/folders/${updateFolderPayload.folderId}`, { name: updateFolderPayload.newName });
      yield put(loadFolder(undefined));
      yield put(showToast({ message: 'projects.toast.folderUpdated', type: 'success' }));
    } catch (error: unknown) {
      const errorMessage =
        isAxiosError(error) && error.response?.data ? error.response.data : 'projects.toast.folderUpdateError';
      yield put(showToast({ message: errorMessage, type: 'error' }));
    }
  });
}

function* createFolderAction(action: PayloadAction<CreateFolderPayload>) {
  const createFolderPayload = action.payload;
  yield call(withLoading, 'createFolder', function* () {
    try {
      yield call(httpPOST, `/folders`, {
        name: createFolderPayload.folderName,
        parentId: createFolderPayload.parentId,
      });

      const loadFolderId = createFolderPayload?.parentId ? createFolderPayload.parentId : undefined;

      yield put(loadFolder(loadFolderId));
      yield put(showToast({ message: 'projects.toast.folderCreated', type: 'success' }));
    } catch (error: unknown) {
      const errorMessage =
        isAxiosError(error) && error.response?.data ? error.response.data : 'projects.toast.folderCreateError';
      yield put(showToast({ message: errorMessage, type: 'error' }));
    }
  });
}

function* deleteFolderAction(action: PayloadAction<number>) {
  const folderId = action.payload;
  yield call(withLoading, 'deleteFolder', function* () {
    try {
      yield call(httpDELETE, `/folders/${folderId}`, {});
      yield put(showToast({ message: 'projects.toast.folderDeleted', type: 'success' }));
      yield put(loadFolder(undefined));
    } catch (_error) {
      yield put(showToast({ message: 'projects.toast.folderDeleteError', type: 'error' }));
    }
  });
}

function* updateProjectNameAction(action: PayloadAction<{ projectId: number; name: string }>) {
  const { projectId, name } = action.payload;
  yield call(withLoading, 'updateProjectName', function* () {
    try {
      yield call(httpPUT, `/projects/${projectId}`, { name });

      const currentProject: Project | null = (yield select(getCurrentProject)) as Project | null;
      if (currentProject && currentProject.id === projectId) {
        const updatedProject = {
          ...currentProject,
          name: name.trim(),
        };
        yield put(setProject(updatedProject));
      }
    } catch (error) {
      console.error('Failed to update project name', error);
      yield put(showToast({ message: 'projects.toast.projectNameUpdateError', type: 'error' }));
    }
  });
}

function* updateProjectTemplateAction(action: PayloadAction<UpdateProjectTemplatePayload>) {
  const { projectId, template } = action.payload;
  yield call(withLoading, 'updateProjectTemplate', function* () {
    try {
      const { data } = (yield call(
        httpPUT,
        `/projects/${projectId}/template?template=${template}`,
        {},
      )) as AxiosResponse<Project>;

      yield put(setProjectById({ project: data, template }));

      const currentProject: Project | null = (yield select(getCurrentProject)) as Project | null;
      if (currentProject && currentProject.id === projectId) {
        yield put(setProject(data));
      }

      yield put(
        showToast({
          message: template ? 'projects.toast.projectSetAsTemplate' : 'projects.toast.projectUnsetAsTemplate',
          type: 'success',
        }),
      );
    } catch (error: unknown) {
      const errorMessage =
        isAxiosError(error) && error.response?.data ? error.response.data : 'projects.toast.updateError';
      yield put(showToast({ message: errorMessage, type: 'error' }));
    }
  });
}

export default function* projectsSaga() {
  yield all([takeLatest(loadProjects, loadProjectsAction)]);
  yield all([takeLatest(loadMoreProjects, loadMoreProjectsAction)]);
  yield all([takeLatest(loadExampleProjects, loadExampleProjectsAction)]);
  yield all([takeEvery(cloneExampleProject, cloneExampleProjectAction)]);
  yield all([takeEvery(createProject, createProjectAction)]);
  yield all([takeEvery(updateProject, updateProjectAction)]);
  yield all([takeEvery(deleteProject, deleteProjectAction)]);
  yield all([takeEvery(moveProject, moveProjectAction)]);
  yield all([takeLatest(loadFolder, loadFolderAction)]);
  yield all([takeLatest(loadFolderTree, loadFolderTreeAction)]);
  yield all([takeEvery(createFolder, createFolderAction)]);
  yield all([takeEvery(updateFolder, updateFolderAction)]);
  yield all([takeEvery(deleteFolder, deleteFolderAction)]);
  yield all([takeEvery(updateProjectName, updateProjectNameAction)]);
  yield all([takeEvery(updateProjectTemplate, updateProjectTemplateAction)]);
}
